/* eslint-disable no-unused-vars */
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Layout } from 'blaze-validator';
import toastr from '../toastr';
import Form from '../form/Form';
import { describeItem, getData, removeItem } from '../../actions/DbActions';
import ListView from '../list/ListView';
import LoadingPage from '../LoadingPage';
import { modal } from '../modal/ModalEmitter';
import Socket from '../../services/SocketService';
import config from '../../config';
import ApiService from '../../services/ApiService';
import { getFilter } from '../list/filters';
import { formatValue, getCellValue } from '../../util/grid';

const encodeFilter = filter => {
  if (!filter) {
    return '';
  }
  return window.btoa(JSON.stringify(filter));
};

class DbListContainer extends Component {
  constructor(props) {
    super(props);
    this.state = { currentPageName: '', currentModelName: '', gridLayout: null, filterLayout: null };
    this.subs = [];

    const filter = getFilter(props.name);
    if (filter) {
      this.state.filterLayout = filter.layout;
      this.state.filterFields = filter.fields;
    } else {
      this.state.filterFields = null;
    }

    this.listView = React.createRef();
  }

  componentDidMount() {
    this.refresh();
  }

  componentWillUnmount() {
    this.unsubscribe(this.state.currentModelName);
  }

  getFilterParams() {
    if (!this.state.filterLayout) {
      return;
    }

    const filterParams = [];
    this.state.filterLayout.forEach(field => {
      if (field.data !== undefined && field.data !== '') {
        filterParams.push(this.state.filterFields[field.name]);
      }
    });

    return filterParams;
  }

  handleFilterSubmit = data => {
    modal.close();

    if (!this.state.filterLayout) {
      return;
    }

    const newFilterFields = { ...this.state.filterFields };

    const newLayout = this.state.filterLayout.map(field => {
      field.data = data[field.name];
      newFilterFields[field.name].value = newFilterFields[field.name].process
        ? newFilterFields[field.name].process(field.data)
        : field.data;
      return field;
    });

    this.setState({ filterLayout: newLayout, filterFields: newFilterFields });

    this.props.getData(this.props.name, { filter: encodeFilter(this.getFilterParams()) });
  };

  handleFilter = () => {
    if (!this.state.filterLayout) {
      return;
    }

    modal.open('info', {
      title: 'Filter',
      size: 'md',
      hidePrint: true,
      hideFooter: true,
      style: 'center',
      // headerClass: 'primary',
      message: (
        <Form
          layout={this.state.filterLayout}
          exportFormat="object"
          onCancel={modal.close}
          submit={this.handleFilterSubmit}
          submitAll
        />
      ),
    });
  };

  handleFilterClear = name => {
    const { filterLayout } = this.state;

    const newFilterFields = { ...this.state.filterFields };

    const newLayout = filterLayout.map(field => {
      if (name === field.name) {
        field.data = '';
        return field;
      }

      newFilterFields[field.name].value = newFilterFields[field.name].process
        ? newFilterFields[field.name].process(field.data)
        : field.data;

      return field;
    });

    this.setState({ filterLayout: newLayout, filterFields: newFilterFields });

    this.props.getData(this.props.name, { filter: encodeFilter(this.getFilterParams()) });
  };

  getColumnDefs() {
    const { pageLayout } = this.props.meta;
    const layoutObj = new Layout(pageLayout);
    const fieldLookup = layoutObj.getLookup();

    const columnDefs = [];
    let hasSort = false;
    for (const field of this.props.meta.page.headers) {
      if (field.sort) {
        hasSort = true;
      }

      const { style, name, label, hidden, ...rest } = field;
      const column = {
        cellClass: field.style || '',
        field: field.name,
        filter: field.filter,
        headerName: field.label,
        hide: field.hidden,
        ...rest,
      };

      if (
        field.type &&
        _.intersection(typeof field.type === 'string' ? [field.type] : field.type, [
          'number',
          'currencyColumn',
          'numberColumn',
        ]).length
      ) {
        column.filter = 'agNumberColumnFilter';
      }

      if (field.type === 'basicDateColumn') {
        column.filter = 'agDateColumnFilter';
      }

      // TODO Not needed in v26
      if (column.filterParams) {
        column.filterParams.newRowsAction = 'keep';
      } else {
        column.filterParams = { newRowsAction: 'keep' };
      }

      // Check for Set Filter in saved layout so the filter type can be updated
      if (_.get(this.state.gridLayout, `layout.f.${column.field}.filterType`) === 'set') {
        column.filter = 'agSetColumnFilter';
      }

      const fieldConfig = fieldLookup[field.name];

      // Any field config items that need to be accessed by the cell renderer
      if (fieldConfig) {
        // Put options on column root so they're available for viewing Label
        if (fieldConfig.options) {
          column.options = fieldConfig.options;
        }
      }

      // Set Editable Params if flag is set
      if (column.editable) {
        if (column.options) {
          column.cellEditor = 'agRichSelect';
          column.cellEditorPopup = true;
          column.cellEditorParams = { values: column.options, formatValue };
        }
      }

      columnDefs.push(column);
    }

    if (columnDefs.length && !hasSort) {
      columnDefs[0].sort = 'asc';
    }

    return columnDefs;
  }

  getGridLayout = () => {
    return ApiService.get('user-grid-layouts', { params: { pageName: this.props.name } }).then(({ data }) => {
      if (data && data.length) {
        this.setState({ gridLayout: data[0].GridLayout });
      }
    });
  };

  subscribe(name) {
    Socket.db.join(name);

    const sockCreate = res => {
      if (this.listView.current) {
        this.listView.current.agGrid.api.updateRowData({ add: [res.data] });
      }
    };

    this.subs.push(Socket.db.on(`${name}.create`, sockCreate, true));

    const sockUpdate = res => {
      if (this.listView.current) {
        this.listView.current.updateRowById(res.data, parseInt(res.id));
      }
    };

    this.subs.push(Socket.db.on(`${name}.update`, sockUpdate, true));

    const sockRemove = ({ id }) => {
      if (this.listView.current) {
        this.listView.current.removeRowById(id);
      }
    };

    this.subs.push(Socket.list.on(`${name}.remove`, sockRemove, true));
  }

  unsubscribe(name) {
    this.subs.forEach(f => f());
    this.subs = [];
    Socket.db.leave(name);
  }

  ready() {
    return this.props.name === this.state.currentPageName;
  }

  refresh = () => {
    const { name, modelName } = this.props;
    const { currentPageName, currentModelName } = this.state;

    return Promise.all([
      this.props.describeItem(name),
      this.props.getData(name, { filter: encodeFilter(this.getFilterParams()) }),
      this.getGridLayout(),
    ]).then(() => {
      if (currentPageName !== name) {
        this.subscribe(modelName);

        if (currentModelName) {
          this.unsubscribe(currentModelName);
        }
      }
      this.setState({ currentPageName: name, currentModelName: modelName });
    });
  };

  handleRemoveItem = (name, itemId) => {
    modal.confirm({
      message: `Delete record '${itemId}'?`,
      headerClass: 'primary',
      callback: () => {
        this.props
          .removeItem(name, itemId)
          .then(res => {
            if (res.payload.data.error) {
              throw new Error(res.payload.data.error);
            }
            this.refresh();
          })
          .catch(error => {
            toastr.error('Error', error.message);
          });
      },
    });
  };

  getCustomOptions = () => {
    if (this.props.name === 'UPT1') {
      return [
        {
          label: 'Download All Files',
          onClick: () => {
            window.location.href = `${config.apiUrl}/print/template/files`;
          },
        },
        {
          label: 'Upload Template Files',
          onClick: () => {
            modal.open('info', {
              title: 'Upload Template Files',
              hidePrint: true,
              hideFooter: true,
              message: (
                <Form
                  hideCancel
                  layout={[
                    {
                      name: 'filenames',
                      label: 'Template File',
                      type: 'file',
                      minListSize: 1,
                      maxListSize: 100,
                      finalPath: 'templates',
                      useOriginalName: true,
                    },
                  ]}
                  exportFormat="object"
                  submit={data => {
                    toastr.success(`Uploaded ${data.filenames.length} files`);
                    modal.close();
                  }}
                />
              ),
            });
          },
        },
      ];
    }

    return [];
  };

  onCellValueChanged = cellEvent => {
    if (cellEvent.source === 'undoUserEdit') {
      return;
    }

    const neededForUpdate = []; // Additional field data to send even though it's not changing
    const payload = {}; // fieldName/key : value

    const itemId = cellEvent.data.id;

    if (!itemId) {
      console.error('Needs a valid id ready');
      return;
    }

    const fieldName = cellEvent.column.colDef.name || cellEvent.column.colDef.field;
    const headerName = cellEvent.column.colDef.headerName;
    const newValue = getCellValue(cellEvent.newValue);
    const oldValue = getCellValue(cellEvent.oldValue);

    if (!fieldName) {
      console.error('Field name is invalid');
    }

    const columns = cellEvent.columnApi.getAllColumns();

    columns.forEach(c => {
      if (c.colDef.requiredForUpdate) {
        neededForUpdate.push({ fieldName: c.colDef.name, columnName: c.colDef.field });
      }
    });

    neededForUpdate.forEach(column => {
      payload[column.fieldName] = column.columnName;
    });

    // Field value that actually changed
    payload[fieldName] = newValue;

    return ApiService.put(`/db/${this.props.name}/${itemId}`, payload)
      .then(() => {
        toastr.success('Cell Updated', `${headerName} from ${oldValue} to ${newValue}`);
      })
      .catch(error => {
        toastr.error('Cell Update Failure', _.get(error, 'response.data.error', error.message));
        cellEvent.node.setDataValue(cellEvent.column, oldValue, 'undoUserEdit');
      });
  };

  render() {
    if (!this.ready()) {
      return <LoadingPage />;
    }

    return (
      <ListView
        ref={this.listView}
        attachments={this.props.attachments}
        canAdd={this.props.canAdd}
        canCopy={this.props.canCopy}
        canDelete={this.props.canDelete}
        canEdit={this.props.canEdit}
        canNotes={this.props.canNotes}
        canRead={this.props.canRead}
        canUpload={this.props.canUpload}
        customOptions={this.getCustomOptions()}
        columnDefs={this.getColumnDefs()}
        filterLayout={this.state.filterLayout}
        handleFilterClear={this.handleFilterClear}
        handleFilter={this.state.filterLayout ? this.handleFilter : null}
        handleRefresh={this.refresh}
        handleRemoveItem={this.handleRemoveItem}
        idFieldName={this.props.idFieldName}
        itemName={this.props.name}
        layout={this.state.gridLayout}
        namespace="db"
        rowData={this.props.data}
        showRowToolbar={this.props.showRowToolbar}
        initialQuery={this.props.query}
        onCellValueChanged={this.onCellValueChanged}
        rowButtonCount={_.get(this.props.meta, 'yaml.page.List.rowButtonCount')}
      />
    );
  }
}

DbListContainer.defaultProps = {
  canAdd: true,
  canCopy: true,
  canDelete: true,
  canEdit: true,
  canRead: false,
  canUpload: false,
  idFieldName: 'id',
  showRowToolbar: true,
};

DbListContainer.propTypes = {
  canAdd: PropTypes.bool,
  canCopy: PropTypes.bool,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  canRead: PropTypes.bool,
  canUpload: PropTypes.bool,
  data: PropTypes.array,
  describeItem: PropTypes.func,
  getData: PropTypes.func,
  idFieldName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  meta: PropTypes.object,
  modelName: PropTypes.string,
  name: PropTypes.string,
  removeItem: PropTypes.func,
  showRowToolbar: PropTypes.bool,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ describeItem, getData, removeItem }, dispatch);
}

function mapStateToProps(state) {
  return {
    currentPageName: state.db.pageName,
    data: state.db.data,
    attachments: state.db.attachments,
    meta: state.db.meta,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DbListContainer);
